import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Youtube, Facebook, Music2 } from "lucide-react";
import { Ornament } from "./Ornament";
import { STREAMS } from "@/data/site";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/subscribers`, { email });
      toast.success(
        data.status === "exists"
          ? "You're already on the list — we'll keep you posted."
          : "You're on the list — first word on every new show."
      );
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sign you up. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="border-t border-amber-500/15 bg-[#05070d]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="font-serif text-3xl sm:text-4xl uppercase tracking-[0.15em] gold-text leading-tight">
              1001 Nights
              <br />
              Orchestra
            </p>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-sm">
              Austin's longest-running Middle Eastern ensemble — music of the Middle East,
              Latin America and the Balkans since 1987.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <a href={STREAMS.youtube} target="_blank" rel="noopener noreferrer" data-testid="social-youtube" aria-label="YouTube" className="text-slate-500 hover:text-amber-300 transition-colors">
                <Youtube size={18} />
              </a>
              <a href={STREAMS.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram" className="text-slate-500 hover:text-amber-300 transition-colors">
                <Instagram size={18} />
              </a>
              <a href={STREAMS.facebook} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" aria-label="Facebook" className="text-slate-500 hover:text-amber-300 transition-colors">
                <Facebook size={18} />
              </a>
              <a href={STREAMS.spotify} target="_blank" rel="noopener noreferrer" data-testid="social-spotify" aria-label="Spotify" className="text-slate-500 hover:text-amber-300 transition-colors">
                <Music2 size={18} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-5">Explore</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/shows" data-testid="footer-link-shows" className="text-slate-400 hover:text-amber-200 transition-colors">Upcoming Shows</Link></li>
              <li><Link to="/media" data-testid="footer-link-media" className="text-slate-400 hover:text-amber-200 transition-colors">Media</Link></li>
              <li><Link to="/about" data-testid="footer-link-about" className="text-slate-400 hover:text-amber-200 transition-colors">About the Band</Link></li>
              <li><Link to="/booking" data-testid="footer-link-booking" className="text-slate-400 hover:text-amber-200 transition-colors">Booking & Inquiries</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-5">Get Announcements</p>
            <form onSubmit={subscribe} className="flex">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input"
                placeholder="your@email.com"
                className="field border-r-0"
                aria-label="Email to get announcements"
              />
              <button
                type="submit"
                data-testid="newsletter-submit-btn"
                className="shrink-0 px-5 bg-gold text-midnight font-mono text-xs uppercase tracking-[0.15em] hover:bg-gold-bright transition-colors"
              >
                Join
              </button>
            </form>
            <p className="mt-6 font-mono text-xs text-slate-500 tracking-widest uppercase">
              Austin, Texas {"\u25C8"} Since 1987
            </p>
          </div>
        </div>

        <Ornament className="mt-14" />
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-600">{"\u00A9"} 2026 1001 Nights Orchestra. All rights reserved.</p>
          <Link to="/admin" data-testid="footer-admin-link" className="text-xs text-slate-700 hover:text-slate-500 transition-colors">
            Admin
          </Link>
        </div>
      </div>
    </footer>
  );
};
